import { useEffect, useState } from 'react';
import { MediaTypes, Persona, SubscriptionPlans } from '@/constants';
import CheckBoxField from '@/shared/components/form/CheckBox';
import DropZoneField from '@/shared/components/form/dropzones/DropZone';
import { MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import TextEditor from '@/shared/components/text-editor/TextEditor';

interface Props {
    initialPayload: Record<string, unknown>;
    onChange: (payload: Record<string, unknown>) => void;
}

/**
 * Shape mirrors what the API's ContentTargetHandler.validatePayload accepts:
 * title / categoryId / description / body / imagePath / mediaPath /
 * mediaType / subscriptionPlan / personas / completionCaption / introSeconds.
 */
const ContentPayloadForm = ({ initialPayload, onChange }: Props) => {
    const [title, setTitle] = useState((initialPayload.title as string) ?? '');
    const [description, setDescription] = useState((initialPayload.description as string) ?? '');
    const [initialBody] = useState((initialPayload.body as string) ?? '');
    const [body, setBody] = useState((initialPayload.body as string) ?? '');
    const [completionCaption, setCompletionCaption] = useState((initialPayload.completionCaption as string) ?? '');
    const [imagePath, setImagePath] = useState((initialPayload.imagePath as string) ?? '');
    const [mediaPath, setMediaPath] = useState((initialPayload.mediaPath as string) ?? '');
    const [mediaType, setMediaType] = useState<MediaTypes>((initialPayload.mediaType as MediaTypes) ?? MediaTypes.Audio);
    const [subscriptionPlan, setSubscriptionPlan] = useState<SubscriptionPlans | ''>((initialPayload.subscriptionPlan as SubscriptionPlans) ?? '');
    const [personas, setPersonas] = useState<Persona[]>((initialPayload.personas as Persona[]) ?? []);
    const [introSeconds, setIntroSeconds] = useState<number | ''>(
        typeof initialPayload.introSeconds === 'number' ? (initialPayload.introSeconds as number) : '',
    );

    // `onChange` is omitted from the deps because it's the parent's setState
    // (stable identity); including it would either need to be `useCallback`-wrapped
    // upstream or would just retrigger this effect on every parent render.
    useEffect(() => {
        // Build the payload from non-empty fields only — the API validator rejects
        // empty / unknown keys, and we don't want to send `subscriptionPlan: ''`.
        const payload: Record<string, unknown> = {};
        if (title) payload.title = title;
        if (description) payload.description = description;
        if (body) payload.body = body;
        if (completionCaption) payload.completionCaption = completionCaption;
        if (imagePath) payload.imagePath = imagePath;
        if (mediaPath) payload.mediaPath = mediaPath;
        if (mediaPath) payload.mediaType = mediaType;
        if (subscriptionPlan) payload.subscriptionPlan = subscriptionPlan;
        if (personas.length) payload.personas = personas;
        if (introSeconds !== '' && typeof introSeconds === 'number') payload.introSeconds = introSeconds;
        onChange(payload);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, description, body, completionCaption, imagePath, mediaPath, mediaType, subscriptionPlan, personas, introSeconds]);

    return (
        <div>
            <MaterialTextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <MaterialTextField label="Description" multiline value={description} onChange={(e) => setDescription(e.target.value)} />
            <div>
                <p>Body</p>
                <TextEditor initialContent={initialBody} onChange={(c: string) => setBody(c)} />
            </div>
            <MaterialTextField
                label="Completion Caption"
                multiline
                value={completionCaption}
                onChange={(e) => setCompletionCaption(e.target.value)}
            />
            <div>
                <p>Cover Image</p>
                <DropZoneField
                    customHeight
                    accept="image/jpeg, image/jpg, image/png"
                    currentValue={imagePath}
                    currentFileType="image"
                    onChange={(p) => setImagePath(p)}
                />
            </div>
            <div className="mt-3">
                <p>Media</p>
                <DropZoneField
                    customHeight
                    accept="audio/mp3, audio/mpeg, video/mp4"
                    currentValue={mediaPath}
                    currentFileType={mediaType}
                    onChange={(p, t) => {
                        setMediaPath(p);
                        const parsed = (t as string).split('/')[0].trim() as MediaTypes;
                        if (parsed) setMediaType(parsed);
                    }}
                />
            </div>
            <MaterialTextField
                name="select"
                select
                label="Subscription Plan (optional override)"
                value={subscriptionPlan}
                onChange={(e) => setSubscriptionPlan(e.target.value as SubscriptionPlans | '')}
            >
                <MaterialFormOption value="">— Keep target's current plan —</MaterialFormOption>
                <MaterialFormOption value={SubscriptionPlans.Free}>{SubscriptionPlans.Free}</MaterialFormOption>
                <MaterialFormOption value={SubscriptionPlans.Premium}>{SubscriptionPlans.Premium}</MaterialFormOption>
            </MaterialTextField>
            <div style={{ marginBottom: 12 }}>
                <p style={{ marginBottom: 6 }}>Personas (optional override)</p>
                <div style={{ display: 'flex', gap: 16 }}>
                    {Object.values(Persona).map((p) => (
                        <CheckBoxField
                            key={p}
                            name={`persona_${p}`}
                            checked={personas.includes(p)}
                            onChange={() => setPersonas((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]))}
                            label={p.charAt(0).toUpperCase() + p.slice(1)}
                        />
                    ))}
                </div>
            </div>
            <MaterialTextField
                label="Intro seconds (video only, for Skip Intro button)"
                type="number"
                value={introSeconds}
                onChange={(e) => setIntroSeconds(e.target.value === '' ? '' : Number(e.target.value))}
            />
        </div>
    );
};

export default ContentPayloadForm;
